
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { participantsAPI } from '../lib/api';
import { Search, Plus, UserRound, AlertTriangle, Wallet, Edit, Trash2, X } from 'lucide-react';

const emptyForm = {
  full_name: '',
  date_of_birth: '',
  ndis_number: '',
  phone: '',
  email: '',
  address: '',
  primary_disability: '',
  support_level: '',
  plan_start_date: '',
  plan_end_date: '',
  funding_total: '',
  funding_used: '',
  goals_text: '',
  allergies_text: '',
  medications_text: '',
  risk_flags_text: '',
  notes: '',
};

function splitLines(value) {
  return (value || '')
    .split('\n')
    .map(v => v.trim())
    .filter(Boolean);
}

function joinLines(value) {
  return Array.isArray(value) ? value.join('\n') : '';
}

export default function Participants() {
  const [participants, setParticipants] = useState([]);
  const [summary, setSummary] = useState(null);
  const [search, setSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [listRes, summaryRes] = await Promise.all([
        participantsAPI.getAll({ search: search || undefined, include_inactive: includeInactive }),
        participantsAPI.summary().catch(() => ({ data: null })),
      ]);
      setParticipants(listRes.data || []);
      setSummary(summaryRes.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Unable to load participants');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(loadData, 300);
    return () => clearTimeout(timer);
  }, [search, includeInactive]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      full_name: p.full_name || '',
      date_of_birth: p.date_of_birth || '',
      ndis_number: p.ndis_number || '',
      phone: p.phone || '',
      email: p.email || '',
      address: p.address || '',
      primary_disability: p.primary_disability || '',
      support_level: p.support_level || '',
      plan_start_date: p.plan_start_date || '',
      plan_end_date: p.plan_end_date || '',
      funding_total: p.funding_total ?? '',
      funding_used: p.funding_used ?? '',
      goals_text: joinLines(p.goals),
      allergies_text: joinLines(p.allergies),
      medications_text: joinLines(p.medications),
      risk_flags_text: joinLines(p.risk_flags),
      notes: p.notes || '',
    });
    setShowForm(true);
  };

  const payloadFromForm = () => ({
    full_name: form.full_name,
    date_of_birth: form.date_of_birth || null,
    ndis_number: form.ndis_number || null,
    phone: form.phone || null,
    email: form.email || null,
    address: form.address || null,
    primary_disability: form.primary_disability || null,
    support_level: form.support_level || null,
    plan_start_date: form.plan_start_date || null,
    plan_end_date: form.plan_end_date || null,
    funding_total: Number(form.funding_total || 0),
    funding_used: Number(form.funding_used || 0),
    goals: splitLines(form.goals_text),
    allergies: splitLines(form.allergies_text),
    medications: splitLines(form.medications_text),
    risk_flags: splitLines(form.risk_flags_text),
    notes: form.notes || null,
  });

  const saveParticipant = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim()) {
      setError('Participant name is required');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const payload = payloadFromForm();
      if (editing) {
        await participantsAPI.update(editing.id, payload);
      } else {
        await participantsAPI.create(payload);
      }
      setShowForm(false);
      setEditing(null);
      setForm(emptyForm);
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Unable to save participant');
    } finally {
      setSaving(false);
    }
  };

  const deactivateParticipant = async (p) => {
    if (!window.confirm(`Deactivate ${p.full_name}?`)) return;
    try {
      await participantsAPI.delete(p.id);
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Unable to deactivate participant');
    }
  };

  const money = (v) => new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0,
  }).format(Number(v || 0));

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Participants</h1>
          <p className="text-slate-500">NDIS participant profiles, funding, goals, risks and care notes.</p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-white hover:bg-slate-800"
        >
          <Plus size={18} /> Add Participant
        </button>
      </div>

      {summary && (
        <div className="grid gap-4 md:grid-cols-4">
          <div className="rounded-2xl border bg-white p-5 shadow-sm">
            <UserRound className="mb-3 text-emerald-500" />
            <div className="text-2xl font-bold">{summary.active_participants || 0}</div>
            <div className="text-sm text-slate-500">Active Participants</div>
          </div>
          <div className="rounded-2xl border bg-white p-5 shadow-sm">
            <AlertTriangle className="mb-3 text-amber-500" />
            <div className="text-2xl font-bold">{summary.high_risk_participants || 0}</div>
            <div className="text-sm text-slate-500">With Risk Flags</div>
          </div>
          <div className="rounded-2xl border bg-white p-5 shadow-sm">
            <Wallet className="mb-3 text-blue-500" />
            <div className="text-2xl font-bold">{money(summary.funding_used)}</div>
            <div className="text-sm text-slate-500">Funding Used</div>
          </div>
          <div className="rounded-2xl border bg-white p-5 shadow-sm">
            <Wallet className="mb-3 text-purple-500" />
            <div className="text-2xl font-bold">{money(summary.funding_remaining)}</div>
            <div className="text-sm text-slate-500">Funding Remaining</div>
          </div>
        </div>
      )}

      <div className="rounded-2xl border bg-white p-4 shadow-sm">
        <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="relative max-w-md flex-1">
            <Search className="absolute left-3 top-3 text-slate-400" size={18} />
            <input
              className="w-full rounded-xl border py-2 pl-10 pr-3 outline-none focus:ring-2 focus:ring-slate-200"
              placeholder="Search name, NDIS number, phone or email"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            Include inactive
          </label>
        </div>

        {error && <div className="mb-4 rounded-xl bg-red-50 p-3 text-sm text-red-700">{error}</div>}

        {loading ? (
          <div className="py-10 text-center text-slate-500">Loading participants...</div>
        ) : participants.length === 0 ? (
          <div className="py-10 text-center text-slate-500">No participants found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[950px] text-left text-sm">
              <thead>
                <tr className="border-b text-slate-500">
                  <th className="py-3">Participant</th>
                  <th>NDIS Number</th>
                  <th>Support Level</th>
                  <th>Funding</th>
                  <th>Risks</th>
                  <th>Status</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {participants.map((p) => (
                  <tr key={p.id} className="border-b last:border-0">
                    <td className="py-4">
                      <Link to={`/participants/${p.id}`} className="font-semibold text-slate-900 hover:underline">{p.full_name}</Link>
                      <div className="text-slate-500">{p.phone || 'No phone'} · {p.address || 'No address'}</div>
                    </td>
                    <td>{p.ndis_number || '—'}</td>
                    <td>{p.support_level || '—'}</td>
                    <td>
                      <div>{money(p.funding_used)} used</div>
                      <div className="text-slate-500">{money(p.funding_remaining)} remaining</div>
                      <div className="mt-2 h-2 w-32 rounded-full bg-slate-100">
                        <div
                          className="h-2 rounded-full bg-slate-900"
                          style={{ width: `${p.funding_total > 0 ? Math.min(Math.round((p.funding_used / p.funding_total) * 100), 100) : 0}%` }}
                        />
                      </div>
                    </td>
                    <td>
                      {p.risk_flags?.length ? (
                        <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-700">
                          {p.risk_flags.length} flag(s)
                        </span>
                      ) : (
                        <span className="text-slate-400">None</span>
                      )}
                    </td>
                    <td>
                      <span className={`rounded-full px-3 py-1 text-xs font-medium ${
                        p.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
                      }`}>
                        {p.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="text-right">
                      <button onClick={() => openEdit(p)} className="mr-2 rounded-lg p-2 hover:bg-slate-100">
                        <Edit size={17} />
                      </button>
                      <button onClick={() => deactivateParticipant(p)} className="rounded-lg p-2 text-red-600 hover:bg-red-50">
                        <Trash2 size={17} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-xl font-bold">{editing ? 'Edit Participant' : 'Add Participant'}</h2>
              <button onClick={() => setShowForm(false)} className="rounded-lg p-2 hover:bg-slate-100">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={saveParticipant} className="grid gap-4 md:grid-cols-2">
              {[
                ['full_name', 'Full Name *'],
                ['date_of_birth', 'Date of Birth'],
                ['ndis_number', 'NDIS Number'],
                ['phone', 'Phone'],
                ['email', 'Email'],
                ['address', 'Address'],
                ['primary_disability', 'Primary Disability'],
                ['support_level', 'Support Level'],
                ['plan_start_date', 'Plan Start Date'],
                ['plan_end_date', 'Plan End Date'],
                ['funding_total', 'Funding Total'],
                ['funding_used', 'Funding Used'],
              ].map(([key, label]) => (
                <label key={key} className="space-y-1">
                  <span className="text-sm font-medium text-slate-700">{label}</span>
                  <input
                    type={key.includes('date') ? 'date' : key.includes('funding') ? 'number' : 'text'}
                    className="w-full rounded-xl border px-3 py-2 outline-none focus:ring-2 focus:ring-slate-200"
                    value={form[key]}
                    onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  />
                </label>
              ))}

              <label className="space-y-1 md:col-span-2">
                <span className="text-sm font-medium text-slate-700">Goals — one per line</span>
                <textarea className="h-20 w-full rounded-xl border px-3 py-2" value={form.goals_text} onChange={(e) => setForm({ ...form, goals_text: e.target.value })} />
              </label>

              <label className="space-y-1">
                <span className="text-sm font-medium text-slate-700">Allergies — one per line</span>
                <textarea className="h-20 w-full rounded-xl border px-3 py-2" value={form.allergies_text} onChange={(e) => setForm({ ...form, allergies_text: e.target.value })} />
              </label>

              <label className="space-y-1">
                <span className="text-sm font-medium text-slate-700">Medications — one per line</span>
                <textarea className="h-20 w-full rounded-xl border px-3 py-2" value={form.medications_text} onChange={(e) => setForm({ ...form, medications_text: e.target.value })} />
              </label>

              <label className="space-y-1 md:col-span-2">
                <span className="text-sm font-medium text-slate-700">Risk Flags — one per line</span>
                <textarea className="h-20 w-full rounded-xl border px-3 py-2" value={form.risk_flags_text} onChange={(e) => setForm({ ...form, risk_flags_text: e.target.value })} />
              </label>

              <label className="space-y-1 md:col-span-2">
                <span className="text-sm font-medium text-slate-700">Notes</span>
                <textarea className="h-24 w-full rounded-xl border px-3 py-2" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </label>

              <div className="flex justify-end gap-3 md:col-span-2">
                <button type="button" onClick={() => setShowForm(false)} className="rounded-xl border px-4 py-2">
                  Cancel
                </button>
                <button disabled={saving} type="submit" className="rounded-xl bg-slate-900 px-4 py-2 text-white disabled:opacity-60">
                  {saving ? 'Saving...' : 'Save Participant'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
